import { AfterViewInit, Component, computed, effect, ElementRef, inject, QueryList, signal, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { LoadAllCheckpoints } from '@/store/checkpoint/checkpoint.actions';
import { FormArray } from '@angular/forms';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import {
  AssessmentPresetMergeDto,
  AssessmentPresetService,
  CheckpointDetailDto,
  CheckpointMergeDto,
  CheckpointService,
  TextBlockMergeDto,
  TextBlockService,
} from '@artemis-software/wr-api';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import { NbDialogService, NbTabComponent, NbToastrService } from '@nebular/theme';
import { PendingChanges } from '@guards/pending-changes.guard';
import { CheckpointFormWrapper } from '@pages/checkpoint-detail-page/checkpoint-form';
import {
  CheckpointFormType,
  createAssessmentPresetForm,
  createCheckpointForm
} from '@pages/checkpoint-detail-page/checkpoint-utils';
import { currentTenantId } from '@/utils/admin-utils';
import { AssessmentDetailFormComponent } from '@pages/checkpoint-detail-page/assessment-detail-form/assessment-detail-form.component';

type TabId = 'general' | 'presets';

@Component({
  standalone: false,
  selector: 'wr-checkpoint-detail-page',
  templateUrl: './checkpoint-detail-page.component.html',
  styleUrls: ['./checkpoint-detail-page.component.scss'],
})
export class CheckpointDetailPageComponent implements PendingChanges, AfterViewInit {
  @ViewChildren('text') private apTexts!: QueryList<ElementRef<HTMLTextAreaElement>>;
  @ViewChild(AssessmentDetailFormComponent) private assessmentDetailForm?: AssessmentDetailFormComponent;

  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly checkpointService = inject(CheckpointService);
  private readonly assessmentPresetService = inject(AssessmentPresetService);
  private readonly textBlockService = inject(TextBlockService);
  private readonly dialogService = inject(NbDialogService);
  private readonly toastrService = inject(NbToastrService);

  private pendingFocusIndex: number | null = null;
  isEditing = false;
  firstSubmit = false;
  formWrapper: CheckpointFormWrapper = new CheckpointFormWrapper();
  saving$ = new BehaviorSubject(false);
  checkpointId: string | null = null;

  currentTenantId = currentTenantId();

  readonly toDeleteAssessmentPreset = signal<string[]>([]);
  readonly toDeleteTextBlocks = signal<string[]>([]);
  readonly activeTabId = signal<TabId>('general');
  readonly canEdit = signal(false);
  readonly isNew = computed(() => this.checkpointId && this.checkpointId === 'new');
  private readonly checkpointTenantId = signal<string | undefined>(undefined);
  readonly catalogTenantId = computed(() => this.checkpointTenantId() ?? this.currentTenantId());

  constructor() {
    const route = inject(ActivatedRoute);
    route.paramMap.subscribe((params) => {
      const id = this.checkpointId = params.get('id');
      this.checkpointTenantId.set(undefined);
      const saved = this.readSavedTab();
      if (saved) this.activeTabId.set(saved);
      if (id) {
        if (id === 'new') {
          this.canEdit.set(true);
          this.isEditing = true;
          this.initForm();
        } else {
          this.checkpointService.findById(id).subscribe((checkpoint) => {
            this.checkpointTenantId.set(checkpoint.tenant?.id);
            this.canEdit.set(checkpoint.permissions.canEdit);
            this.initForm(checkpoint);
            this.formWrapper.form().disable();
          });
        }
      } else {
        this.router.navigate(['checkpoints']);
      }
    });
    effect(() => {
      const id = this.checkpointId;
      if (id) {
        this.saveTab(this.activeTabId());
      }
    });
  }

  initForm(dto?: CheckpointDetailDto): void {
    const form = createCheckpointForm(dto);

    if(dto) {
      this.createAssessmentPresets(form, dto);
    }
    else {
      const newAssessmentPreset = createAssessmentPresetForm();
      this.formWrapper.keepStandardAssessmentExclusive(newAssessmentPreset);
      form.controls.assessmentPresets.push(newAssessmentPreset);
    }

    this.formWrapper.setForm(form);
  }

  createAssessmentPresets(form: CheckpointFormType, checkpoint: CheckpointDetailDto) {
    const formArray = form.controls?.assessmentPresets;

    if(!formArray) {
      throw new Error('Assessment presets not found');
    }

    const presets = checkpoint.assessmentPresets;
    const listedStandardId = presets.find((preset) => preset.standardAssessment)?.id;
    presets.forEach((preset) => {
      const presetForm = createAssessmentPresetForm({
        ...preset,
        standardAssessment: preset.id === listedStandardId,
      });
      this.formWrapper.keepStandardAssessmentExclusive(presetForm);
      formArray.push(presetForm);
    });
  }


  ngAfterViewInit(): void {
    this.apTexts.changes.subscribe((list: QueryList<ElementRef<HTMLTextAreaElement>>) => {
      if (this.pendingFocusIndex === null) return;

      Promise.resolve().then(() => {
        const els = list.toArray();
        const idx = Math.max(0, Math.min(this.pendingFocusIndex!, els.length - 1));
        const el = els[idx]?.nativeElement;
        if (el) {
          el.focus();
          const len = el.value?.length ?? 0;
          el.setSelectionRange(len, len);
        }
        this.pendingFocusIndex = null;
      });
    });
  }

  onEdit(): void {
    this.isEditing = true;
    this.formWrapper.enable();
  }

  async cancel(): Promise<void> {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate(['checkpoint', this.formWrapper.form().value.id]);
    });
  }

  async navigateBack(): Promise<void> {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      await this.router.navigate(['checkpoints']);
    }
  }

  async submit(): Promise<void> {
    this.firstSubmit = true;
    this.formWrapper.markAllAsTouched();

    if (!this.formWrapper.valid()) {
      this.assessmentDetailForm?.expandInvalidTextBlockSections();
      this.toastrService.danger('Prüfpunkt kann nicht gespeichert werden, es gibt noch Fehler.', 'Prüfpunkt');
      return;
    }

    try {
      this.saving$.next(true);
      const checkpointDto = await this.formWrapper.getMappedValue() as CheckpointMergeDto;
      const checkpoint = await firstValueFrom(this.checkpointService.merge(checkpointDto));

      await Promise.all([
        ...this.toDeleteAssessmentPreset().map(id => firstValueFrom(this.assessmentPresetService.deleteById(id))),
        ...this.toDeleteTextBlocks().map(id => firstValueFrom(this.textBlockService.deleteById(id))),
      ]);
      this.toDeleteAssessmentPreset.set([]);
      this.toDeleteTextBlocks.set([]);

      const mappedValue = await this.formWrapper.getMappedValue();
      const assessmentPresets = mappedValue.assessmentPresets as (AssessmentPresetMergeDto & { textBlocks?: TextBlockMergeDto[] })[];
      const presetControls = this.formWrapper.form().controls.assessmentPresets.controls;

      // Nach jedem Merge die ids ins Formular zurückschreiben: schlägt ein späterer
      // Teil des Speicherns fehl, erzeugt der Retry sonst Duplikate (id == null → create).
      const mergeAssessmentPresetPromises = assessmentPresets.map(async ({ textBlocks, ...assessmentPreset }, presetIndex) => {
        assessmentPreset.checkpointId = checkpoint.id;
        const savedPreset = await firstValueFrom(this.assessmentPresetService.merge(assessmentPreset));
        presetControls[presetIndex]?.get('id')?.setValue(savedPreset.id, { emitEvent: false });
        if (!textBlocks?.length) {
          return;
        }
        const savedTextBlocks = await firstValueFrom(this.textBlockService.mergeAll(
          textBlocks.map((textBlock, index) => ({
            ...textBlock,
            ranking: index,
            assessmentPresetId: savedPreset.id,
          }))
        ));
        const textBlockControls = (presetControls[presetIndex]?.get('textBlocks') as FormArray | null)?.controls ?? [];
        savedTextBlocks.forEach((savedTextBlock, index) =>
          textBlockControls[index]?.get('id')?.setValue(savedTextBlock.id, { emitEvent: false }));
      });

      await Promise.all(mergeAssessmentPresetPromises);

      this.isEditing = false;
      this.store.dispatch(new LoadAllCheckpoints());
      await this.router.navigateByUrl('/', { skipLocationChange: true });
      await this.router.navigate(['checkpoint', checkpoint.id], {
        replaceUrl: true,
        state: { tabId: this.activeTabId() },
      });
      this.toastrService.success('Prüftpunkt wurde erfolgreich gespeichert', 'Prüftpunkt');
    } catch (e) {
      console.error(e);
      this.toastrService.danger('Prüfpunkt konnte nicht gespeichert werden', 'Fehler');
    } finally {
      this.saving$.next(false);
    }
  }

  onDelete(): void {
    this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Prüfpunkt löschen',
        message: 'Bist du sicher, dass du diesen Prüfpunkt löschen willst?',
      },
    }).onClose.subscribe((result) => {
      if (result) {
        if (this.isNew()) {
          this.router.navigate(['checkpoints']);
          return;
        }
        this.checkpointService.deleteById(this.formWrapper.form().value.id!).subscribe(() => {
          this.store.dispatch(new LoadAllCheckpoints());
          this.router.navigate(['checkpoints']);
          this.toastrService.success('Prüfpunkt wurde gelöscht', 'Prüfpunkt');
        });
      }
    }, () => {
      this.toastrService.danger('Prüfpunkt konnte nicht gelöscht werden.', 'Prüfpunkt');
    });
  }

  hasPendingChanges(): boolean | Promise<boolean> {
    return this.isEditing;
  }

  onTabChange(tab: NbTabComponent) {
    this.activeTabId.set((tab.tabId as TabId) ?? 'general');
  }

  private tabKey(): string {
    return `checkpoint:${this.checkpointId ?? 'new'}:activeTab`;
  }

  private readSavedTab(): TabId | null {
    try {
      return (sessionStorage.getItem(this.tabKey()) as TabId) ?? null;
    } catch {
      return null;
    }
  }

  private saveTab(tab: TabId): void {
    try {
      sessionStorage.setItem(this.tabKey(), tab);
    } catch {
      console.warn('Could not save active tab to sessionStorage');
    }
  }

  protected readonly signal = signal;
}
